import { useEffect, useState } from "react";
import { browserLocalPersistence, GoogleAuthProvider, isSignInWithEmailLink, onAuthStateChanged, sendSignInLinkToEmail, setPersistence, signInWithEmailLink, signInWithPopup, User } from "firebase/auth";
import { useParams } from "react-router-dom";
import { auth } from "../Lib/Firebase";
import { listOpenCourseRuns, listPublicInternshipRuns, listPublicWorkshopRuns } from "../Lib/courseApi";
import { createCoursePaymentOrder, verifyCoursePayment } from "../Lib/enrollmentApi";
import { CheckoutContact, checkoutContactError, loadCheckoutContact, saveCheckoutContact } from "../Lib/conversionFunnel";
import { trackFunnelEvent } from "../Lib/funnelAnalytics";
import { friendlyAuthError } from "../StaticComponents/studentAuth/types";
import CourseCheckoutView, { CheckoutCourse } from "./checkout/CourseCheckoutView";
import "../themes/student-theme.css";
declare global { interface Window { Razorpay?: new (options: Record<string, unknown>) => { open: () => void; on?: (event: string, callback: (result: unknown) => void) => void }; } }
function loadRazorpay() {
    if (window.Razorpay) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => {
        const existing = document.querySelector<HTMLScriptElement>('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
        if (existing) { existing.addEventListener("load", () => resolve(true), { once: true }); existing.addEventListener("error", () => resolve(false), { once: true }); return; }
        const script = document.createElement("script"); script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true); script.onerror = () => resolve(false); document.body.appendChild(script);
    });
}
const CHECKOUT_EMAIL_KEY = "mockzo_checkout_email";
export default function CourseCheckout() {
    const { courseRunId = "" } = useParams();
    const [user, setUser] = useState<User | null>(auth.currentUser);
    const [course, setCourse] = useState<CheckoutCourse | null>(null);
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [message, setMessage] = useState("");
    const [contact, setContact] = useState<CheckoutContact>({ name: "", email: "", termsAccepted: false });
    const [contactLoading, setContactLoading] = useState(true);
    const [authEmail, setAuthEmail] = useState("");
    const [authError, setAuthError] = useState("");
    const [authSent, setAuthSent] = useState(false);
    const [authBusy, setAuthBusy] = useState(false);
    const [completingLink, setCompletingLink] = useState(false);
    useEffect(() => onAuthStateChanged(auth, (current) => {
        setUser(current);
        if (!current) {
            setContactLoading(false);
            return;
        }
        setAuthEmail(current.email || "");
        loadCheckoutContact(current).then(setContact).catch(() => setContact((value) => ({ ...value, name: current.displayName || "", email: current.email || "" }))).finally(() => setContactLoading(false));
    }), []);

    useEffect(() => {
        if (!isSignInWithEmailLink(auth, window.location.href)) return;
        const stored = window.localStorage.getItem(CHECKOUT_EMAIL_KEY) || "";
        setCompletingLink(true); setAuthEmail(stored);
        if (!stored) return;
        setAuthBusy(true);
        signInWithEmailLink(auth, stored, window.location.href).then(() => { window.localStorage.removeItem(CHECKOUT_EMAIL_KEY); window.history.replaceState({}, "", window.location.pathname); trackFunnelEvent("auth_complete", { method: "email_link", source: "course_checkout" }); }).catch((cause) => setAuthError(friendlyAuthError(cause, "This sign-in link is invalid or expired."))).finally(() => setAuthBusy(false));
    }, []);

    useEffect(() => {
        let active = true; setCourse(null); setLoading(true);
        Promise.allSettled([listPublicWorkshopRuns(), listPublicInternshipRuns()]).then(([workshops, internships]) => {
            if (!active) return;
            const workshop = workshops.status === "fulfilled" ? workshops.value.open.find((item) => item.id === courseRunId && item.enrollmentMode === "PAID") : undefined;
            const internship = internships.status === "fulfilled" ? internships.value.open.find((item) => item.id === courseRunId && item.enrollmentMode === "PAID") : undefined;
            const item = workshop || internship;
            setCourse((current) => item ? { ...item, kind: workshop ? "WORKSHOP" : "INTERNSHIP", alreadyEnrolled: current?.id === item.id ? current.alreadyEnrolled : false } : current);
        }).finally(() => active && setLoading(false));
        return () => { active = false; };
    }, [courseRunId]);

    useEffect(() => {
        if (!user || !courseRunId) return;
        listOpenCourseRuns().then((courses) => {
            const item = courses.find((candidate) => candidate.id === courseRunId && candidate.enrollmentMode === "PAID");
            if (item) setCourse({ ...item, kind: item.courseType === "INTERNSHIP" ? "INTERNSHIP" : item.courseType === "WORKSHOP" ? "WORKSHOP" : "COURSE" });
        }).catch(() => undefined);
    }, [courseRunId, user]);

    const emailAuthentication = async () => {
        const normalized = authEmail.trim().toLowerCase();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) { setAuthError("Enter a valid email address."); return; }
        setAuthBusy(true); setAuthError(""); trackFunnelEvent("auth_start", { method: "email_link", source: "course_checkout" });
        try {
            if (completingLink) {
                await signInWithEmailLink(auth, normalized, window.location.href); window.localStorage.removeItem(CHECKOUT_EMAIL_KEY); window.history.replaceState({}, "", window.location.pathname); trackFunnelEvent("auth_complete", { method: "email_link", source: "course_checkout" });
            } else {
                await sendSignInLinkToEmail(auth, normalized, { url: `${window.location.origin}${window.location.pathname}`, handleCodeInApp: true }); window.localStorage.setItem(CHECKOUT_EMAIL_KEY, normalized); setAuthSent(true);
            }
        } catch (cause) { setAuthError(friendlyAuthError(cause, "Email sign-in could not be completed.")); }
        finally { setAuthBusy(false); }
    };
    const googleAuthentication = async () => {
        setAuthBusy(true); setAuthError(""); trackFunnelEvent("auth_start", { method: "google", source: "course_checkout" });
        try { await setPersistence(auth, browserLocalPersistence); const provider = new GoogleAuthProvider(); provider.addScope("email"); provider.addScope("profile"); await signInWithPopup(auth, provider); trackFunnelEvent("auth_complete", { method: "google", source: "course_checkout" }); }
        catch (cause) { setAuthError(friendlyAuthError(cause, "Google sign-in could not be completed.")); }
        finally { setAuthBusy(false); }
    };

    const dashboard = (status: "success" | "failed") => window.location.replace(`/dashboard?payment=${status}`);
    const startPayment = async () => {
        if (!user || !course) return;
        const error = checkoutContactError(contact);
        if (error) { setMessage(error); return; }
        setPaying(true); setMessage("");
        try {
            await saveCheckoutContact(user, contact);
            trackFunnelEvent("checkout_start", { productType: course.kind, productId: course.id, amount: course.priceINR });
            const order = await createCoursePaymentOrder({ courseRunId });
            if (!(await loadRazorpay()) || !window.Razorpay) throw new Error("Razorpay checkout could not be loaded. Check your internet connection.");
            const checkout = new window.Razorpay({ key: order.keyId, amount: order.amount, currency: order.currency, name: "MockZo", description: `${course.courseTitle} — ${course.runTitle}`, order_id: order.orderId,
                prefill: { name: contact.name, email: contact.email }, hidden: { email: true }, theme: { color: "#18222d" },
                handler: async (result: any) => { try { await verifyCoursePayment({ razorpayOrderId: result.razorpay_order_id, razorpayPaymentId: result.razorpay_payment_id, razorpaySignature: result.razorpay_signature }); trackFunnelEvent("payment_success", { productType: course.kind, productId: course.id, amount: course.priceINR }); dashboard("success"); } catch { trackFunnelEvent("payment_failed", { productType: course.kind, productId: course.id, stage: "verification" }); dashboard("failed"); } },
                modal: { ondismiss: () => setPaying(false) },
            });
            checkout.on?.("payment.failed", () => { trackFunnelEvent("payment_failed", { productType: course.kind, productId: course.id, stage: "gateway" }); dashboard("failed"); });
            checkout.open();
        } catch (cause) { setMessage(cause instanceof Error ? cause.message : "Payment setup is not available yet."); setPaying(false); }
    };

    return <CourseCheckoutView course={course} loading={loading} signedIn={Boolean(user)} email={user?.email || authEmail} authError={authError} authSent={authSent} authBusy={authBusy} completingLink={completingLink} contact={contact} contactLoading={contactLoading} paying={paying} message={message} onEmail={(value) => { setAuthEmail(value); setAuthSent(false); }} onEmailAction={emailAuthentication} onGoogle={googleAuthentication} onContact={setContact} onPay={startPayment} />;
}
